import { Stack, router, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent, ScrollViewProps, View } from 'react-native';

import { getScreenTitleHeader } from '@fhs/routing/src/components/get-screen-title-header';
import {
  Button,
  ImageAssetWithAltText,
  ListItemGroup,
  ListItemGroupSection,
  ListItemLinkPressable,
  YStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { NutritionalInformation } from '@fhs/ui/src/components/nutritional-information';
import { SodiumDisclaimer } from '@fhs/ui/src/components/nutritional-information/sodium-disclaimer';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useStore } from '@fhs-legacy/frontend/src/state/store/hooks';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { useMenuPickerQuery } from '../queries/use-menu-picker-query';
import { useMenuQueryOpts } from '../queries/use-menu-query-opts';
import { ScreenCustomizeItem } from '../screens/screen-customize-item';
import { useComboSelectionContext } from '../state/combo-selection';
import { useMenuPicker } from '../state/menu-picker';
import type { EditComboItem } from '../types';
import { formatCentsToDollars, pickerAspectOptionSubtitle } from '../utils';
import { useComboSelectionEvents } from '../utils/use-combo-selection-events';
import { getComboUnitPriceCents } from '../utils/use-menu-item-price-preview';
import { useResetCustomizationsAlert } from '../utils/use-reset-customizations-alert';

import { ComboGuide } from './combo-guide';
import { CustomizationGroup } from './customization-group';
import { PICKER_SELECTION_TYPE } from './guide/constants';
import { PickerSelectionType } from './guide/types';
import { MakeComboSection } from './make-combo-section';
import { PickerAspectOptionSelector } from './picker-aspect-option-selector';
import { ExtraInstructions } from './product-detail-extra-instructions';
import { ProductDetailLayout } from './product-detail-layout';
import { ProductNameAndImage } from './product-name-and-image';

const enabledStatesForSodiumDisclaimer = ['PA', 'NY'];

type NutritionalInformationProps = {
  isShowingNutrition: boolean;
  setIsShowingNutrition: (isShowingNutrition: boolean) => void;
  showSodiumDisclaimer: boolean;
};

const NutritionalInformationTile = ({
  isShowingNutrition,
  setIsShowingNutrition,
  showSodiumDisclaimer,
}: NutritionalInformationProps) => {
  const { selectedPickerAspectOption, defaultPickerOption, slug } = useMenuPicker();
  const pickerOption = selectedPickerAspectOption?.item ?? defaultPickerOption?.item;

  const menuQueryOpts = useMenuQueryOpts();
  const staticMenuPickerQuery = useMenuPickerQuery({ slug, menuQueryOpts });

  return (
    <View>
      <ListItemGroup
        items={[
          {
            render: ({ isFirstItem, isLastItem }) => (
              <ListItemLinkPressable
                titleFontWeight="normal"
                minHeightStyle="content"
                isFirstItem={isFirstItem}
                isLastItem={isLastItem}
                title="Nutritional Information"
                accessibilityHint="Opens nutritional facts"
                onPress={() => setIsShowingNutrition(true)}
              />
            ),
          },
        ]}
      />

      {showSodiumDisclaimer && <SodiumDisclaimer />}

      <NutritionalInformation
        nutritionFacts={(() => {
          const option = pickerOption?.nutrition
            ? pickerOption
            : staticMenuPickerQuery.data?.pickerAspect?.options?.find(o => o.isDefault)?.item;

          return {
            title: option?.displayName ?? '',
            image: option?.image as ImageAssetWithAltText,
            facts: option?.nutrition?.flatMap(fact => {
              // massage nutrition facts to expected type (ensure required fields exist)
              if (!fact.key || !fact.displayName || !fact.displayValue) {
                return [];
              }

              return { key: fact.key, displayName: fact.displayName, displayValue: fact.displayValue }
            }) ?? []
          }
        })()}
        isVisible={isShowingNutrition}
        onClose={() => setIsShowingNutrition(false)}
        showNutritionixLink={menuQueryOpts.region !== 'ca'}
      />
    </View>
  );
};

type MenuDetailsProps = Omit<NutritionalInformationProps, 'showSodiumDisclaimer'> & {
  handleAddToCart?: () => void;
  renderScrollComponent?: React.ComponentType<ScrollViewProps> | React.FC<ScrollViewProps>;
  editComboItems?: EditComboItem[];
};

export function MenuItemDetails(props: MenuDetailsProps) {
  const mqStyles = useMqStyles();
  const menuPicker = useMenuPicker();
  const {
    keepCustomizationInView,
    shouldShowCustomizationScreen,
    setShouldShowCustomizationScreen,
    selectedPickerAspectOption,
    defaultPickerOption,
  } = menuPicker;
  const { logPickerTypeSelection } = useComboSelectionEvents();
  const {
    setIsEditingPickers,
    isComboAvailable,
    isComboEnabled,
    isComboGuideOpen,
    isGuideLoading,
    setIsComboGuideOpen,
    selectedPickers,
    shouldAddToCartDirectly,
    guideSteps,
    addOrUpdateSelectedPicker,
    pickerSelectionType,
    setPickerSelectionType,
  } = useComboSelectionContext();

  const handlePickerTypeSelection = useCallback(
    (selectionType: PickerSelectionType) => {
      setPickerSelectionType(selectionType);
      logPickerTypeSelection(selectionType);
    },
    [setPickerSelectionType, logPickerTypeSelection]
  );

  const { resetCustomizationsAlertDialog, openResetCustomizationsAlertDialog } =
    useResetCustomizationsAlert();

  // Auto-selects combo items from edit-from-cart (reorder/edit) or required-slot defaults
  useEffect(() => {
    if (!isGuideLoading && guideSteps && !Object.keys(selectedPickers).length) {
      guideSteps.forEach(step => {
        const editComboItem = props.editComboItems?.find(item => item.slotId === step.id);
        const itemIdToSelect = editComboItem?.itemId ?? step.preSelectedItemId;

        if (itemIdToSelect) {
          const itemToPreSelect = step.sections[0].items.find(item => item.id === itemIdToSelect);

          if (itemToPreSelect) {
            const basePriceCents = itemToPreSelect.priceCents ?? 0;
            const modifiersPriceCents = (editComboItem?.selectedCustomizationOptions ?? []).reduce(
              (cents, option) => cents + option.upChargeCents,
              0
            );

            addOrUpdateSelectedPicker(step.id, {
              id: itemToPreSelect.id,
              slug: itemToPreSelect.slug,
              displayName: itemToPreSelect.displayName,
              priceCents: basePriceCents + modifiersPriceCents,
              basePriceCents,
              requiresCustomization: false,
              image: itemToPreSelect.image as ImageAssetWithAltText,
              baseCalories: itemToPreSelect.baseCalories ?? undefined,
              userCustomizationSelections: editComboItem?.customizationSelections,
              resolvedCustomizationSelections: editComboItem?.customizationSelections,
              selectedCustomizationOptions: editComboItem?.selectedCustomizationOptions,
            });
          }
        }
      });
    }
  }, [
    menuPicker.isComboRequired,
    guideSteps,
    isGuideLoading,
    selectedPickers,
    addOrUpdateSelectedPicker,
    props.editComboItems,
  ]);

  const allPickerAspectsAreUnavailable = !selectedPickerAspectOption;

  const extraInstructionsEnabled = false;
  // TODO: determine if order instructions will remain in the menu picker; until then for FHS-4944 disable
  // useFlag(LaunchDarklyFlag.ENABLE_EXTRA_INSTRUCTIONS_ON_ITEM) && !allPickerAspectsAreUnavailable;
  const enableComboDefaultSelection = useFlag('enable-menu-details-default-combo-selection');
  const enableComboLoadingSkeletons = useFlag('enable-simply-menu-details-combo-loading-skeletons');
  const params = useLocalSearchParams<{ slug: string }>();
  const requiresPickAspectOptionSelection = menuPicker.visiblePickerAspectOptions.length > 1;
  const combo = menuPicker.query.data?.combo;

  const { handleAddToCart } = props;

  const customizationGroups =
    menuPicker.selectedPickerAspectOption?.item?.customizations?.displayGroups ?? [];
  const topLevelCustomizationGroups = customizationGroups.filter(
    customizationGroup => customizationGroup.displayGroup.showOnProductPage
  );

  const hasCustomizations = customizationGroups.length > 0;
  const baseCaloriesDisplay = menuPicker.selectedPickerAspectOption?.item?.baseCalories
    ? `${menuPicker.selectedPickerAspectOption?.item?.baseCalories} Cal`
    : null;

  const hasCustomizationsContent = requiresPickAspectOptionSelection || hasCustomizations;
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);

  const [showHeaderTitle, setShowHeaderTitle] = useState(false);
  const [comboGuideInitialStepIndex, setComboGuideInitialStepIndex] = useState(0);
  const onComboGuideClose = useCallback(() => {
    setIsComboGuideOpen(false);
    setIsEditingPickers(false);
  }, [setIsEditingPickers, setIsComboGuideOpen]);

  const onComboGuideFinish = useCallback(() => {
    onComboGuideClose();

    if (shouldAddToCartDirectly) {
      handleAddToCart?.();
    }
  }, [onComboGuideClose, shouldAddToCartDirectly, handleAddToCart]);

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (event?.nativeEvent?.contentOffset?.y > 20) {
      setShowHeaderTitle(true);
    } else {
      setShowHeaderTitle(false);
    }
  }, []);

  const handleCustomizePress = useCallback(() => {
    if (keepCustomizationInView) {
      setShouldShowCustomizationScreen(true);
    } else {
      router.navigate({
        pathname: '/menu/[slug]/customize',
        params: {
          slug: params.slug,
        },
      });
    }
  }, [keepCustomizationInView, setShouldShowCustomizationScreen, params.slug]);

  const { store } = useStore();
  const showSodiumDisclaimer = Boolean(
    store &&
      store?.physicalAddress?.stateProvinceShort &&
      enabledStatesForSodiumDisclaimer.includes(
        store.physicalAddress.stateProvinceShort.toUpperCase()
      )
  );

  const pickerOptions = useMemo(() => {
    const comboData = menuPicker.query.data?.combo;

    if (!comboData || comboData.isRequired || !isComboAvailable) {
      return null;
    }

    const image = (menuPicker.query.data?.image as ImageAssetWithAltText) ?? null;

    const comboOption = {
      value: PICKER_SELECTION_TYPE.COMBO,
      title: comboData?.comboMealLabel ?? '',
      image: (comboData?.comboImage as ImageAssetWithAltText) ?? null,
      bufferImage: enableComboLoadingSkeletons && menuPicker.query.isFetching,
      useSkeletonLoader: enableComboLoadingSkeletons,
      disableImageCache: true,
      subtitle:
        formatCentsToDollars(
          getComboUnitPriceCents({
            menuPickerPriceCents: menuPicker.priceCents ?? 0,
            comboBasePriceCents: comboData?.basePriceCents ?? 0,
            selectedPickers,
          })
        ) ?? '',
      disabled: allPickerAspectsAreUnavailable,
    };

    const justSubOption = {
      value: PICKER_SELECTION_TYPE.JUST_SUB,
      title: comboData?.singleItemMealLabel ?? '',
      image,
      bufferImage: enableComboLoadingSkeletons && menuPicker.query.isFetching,
      useSkeletonLoader: enableComboLoadingSkeletons,
      subtitle: formatCentsToDollars(menuPicker.priceCents ?? 0) ?? '',
      disabled: allPickerAspectsAreUnavailable,
    };

    // We want to change the order of options depending on the default one
    return enableComboDefaultSelection
      ? [comboOption, justSubOption]
      : [justSubOption, comboOption];
  }, [
    menuPicker,
    enableComboDefaultSelection,
    enableComboLoadingSkeletons,
    allPickerAspectsAreUnavailable,
    isComboAvailable,
    selectedPickers,
  ]);

  const { noStoreSelected } = useStoreContext();

  if (shouldShowCustomizationScreen) {
    return <ScreenCustomizeItem renderScrollComponent={props.renderScrollComponent} />;
  }

  const defaultPickerCalories = defaultPickerOption?.item?.baseCalories;
  const baseCalories =
    baseCaloriesDisplay ??
    (typeof defaultPickerCalories === 'number' ? `${defaultPickerCalories} cal` : '');
  const variant =
    menuPicker.selectedPickerAspectOption?.displayName ?? defaultPickerOption?.displayName ?? '';

  const headerTitleOptions = showHeaderTitle
    ? {
        title: menuPicker.displayName,
        header: getScreenTitleHeader({ subtitle: `${variant} • ${baseCalories}` }),
      }
    : { title: '', header: getScreenTitleHeader() };

  return (
    <>
      {!isDesktop && <Stack.Screen options={headerTitleOptions} />}
      <ProductDetailLayout
        renderScrollComponent={props.renderScrollComponent}
        isStaticDetails={noStoreSelected}
        onScroll={onScroll}
        nameAndImageContent={
          <>
            <ProductNameAndImage
              isStaticDetails={noStoreSelected}
              displayName={menuPicker.displayName}
              description={menuPicker.query.data?.description ?? ''}
              showUnavailabilityDisclaimer={allPickerAspectsAreUnavailable}
              variant={variant}
              cals={baseCalories}
              asHero={isDesktop || noStoreSelected || !hasCustomizationsContent}
              imageUrl={menuPicker.query.data?.image?.asset?.uri ?? ''}
              hasCustomizationsContent={hasCustomizationsContent && !noStoreSelected}
              showSodiumDisclaimer={showSodiumDisclaimer}
              heroNutritionalInformationTile={
                (noStoreSelected || !hasCustomizationsContent) && (
                  <NutritionalInformationTile
                    setIsShowingNutrition={props.setIsShowingNutrition}
                    isShowingNutrition={props.isShowingNutrition}
                    showSodiumDisclaimer={showSodiumDisclaimer}
                  />
                )
              }
            />
            {!isDesktop && noStoreSelected && (
              <View
                style={{
                  padding: 16,
                }}
              >
                <View
                  style={{
                    borderTopColor: tokens.colors.$blackOpacity04,
                    borderTopWidth: 1,
                    paddingTop: 32,
                    paddingBottom: 16,
                  }}
                >
                  <NutritionalInformationTile
                    setIsShowingNutrition={props.setIsShowingNutrition}
                    isShowingNutrition={props.isShowingNutrition}
                    showSodiumDisclaimer={showSodiumDisclaimer}
                  />
                </View>
              </View>
            )}
          </>
        }
        customizationContent={
          noStoreSelected
            ? null
            : hasCustomizationsContent && (
                <YStack style={mqStyles.customizationContent}>
                  {pickerOptions && (
                    <ListItemGroupSection heading={menuPicker.query.data?.combo?.selectionLabel}>
                      <PickerAspectOptionSelector
                        value={pickerSelectionType}
                        onChange={handlePickerTypeSelection}
                        options={pickerOptions ?? []}
                      />
                    </ListItemGroupSection>
                  )}
                  {requiresPickAspectOptionSelection && (
                    <ListItemGroupSection
                      heading={menuPicker.query.data?.pickerAspect?.displayName}
                    >
                      <PickerAspectOptionSelector
                        value={menuPicker.selectedPickerAspectOptionId ?? ''}
                        onChange={async selection => {
                          if (menuPicker.checkHasNonDefaultUserCustomization()) {
                            const action = await openResetCustomizationsAlertDialog();
                            if (action === 'cancelled') {
                              return;
                            }
                          }

                          menuPicker.setSelectedPickerAspectOptionId(selection);
                          menuPicker.setDraftCustomizationSelections({});
                        }}
                        options={
                          menuPicker.visiblePickerAspectOptions.map(opt => ({
                            value: opt.id,
                            title: opt.displayName,
                            subtitle: pickerAspectOptionSubtitle(opt),
                            image: opt.image as ImageAssetWithAltText,
                            disabled: !opt?.item?.isAvailable,
                          })) ?? []
                        }
                      />
                    </ListItemGroupSection>
                  )}
                  {hasCustomizations &&
                    topLevelCustomizationGroups.map((customizationGroup, index) => {
                      const isFirstCustomizationGroup = index === 0;

                      return (
                        <CustomizationGroup
                          customizationSelections={menuPicker.resolvedCustomizationSelections}
                          setCustomizationSelections={newSelections => {
                            menuPicker.setUserCustomizationSelections(newSelections);
                            menuPicker.setDraftCustomizationSelections(newSelections);
                          }}
                          key={customizationGroup.displayGroup.id ?? index}
                          customizationGroup={customizationGroup}
                          footer={
                            isFirstCustomizationGroup &&
                            topLevelCustomizationGroups.length < customizationGroups.length && (
                              <Button
                                type="outline"
                                size="md"
                                accessibilityRole="button"
                                accessibilityLabel="Customize ingredients"
                                onPress={handleCustomizePress}
                              >
                                <Button.Text>Customize Ingredients</Button.Text>
                              </Button>
                            )
                          }
                        />
                      );
                    })}

                  {combo && isComboEnabled && !allPickerAspectsAreUnavailable && (
                    <MakeComboSection
                      combo={combo}
                      required={menuPicker.isComboRequired}
                      enabled={isComboEnabled}
                      isLoading={isGuideLoading}
                      onEdit={(stepIndex: number) => {
                        setComboGuideInitialStepIndex(stepIndex);
                        setIsComboGuideOpen(true);
                      }}
                    />
                  )}
                  {isComboGuideOpen && (
                    <ComboGuide
                      steps={guideSteps}
                      onClose={onComboGuideClose}
                      onFinish={onComboGuideFinish}
                      initialStepIndex={comboGuideInitialStepIndex}
                    />
                  )}

                  {extraInstructionsEnabled && (
                    <ExtraInstructions
                      orderInstructions={menuPicker.instructions}
                      onInstructionsChange={menuPicker.setInstructions}
                    />
                  )}

                  <View style={mqStyles.divider} />

                  <NutritionalInformationTile
                    setIsShowingNutrition={props.setIsShowingNutrition}
                    isShowingNutrition={props.isShowingNutrition}
                    showSodiumDisclaimer={showSodiumDisclaimer}
                  />
                </YStack>
              )
        }
      />
      {resetCustomizationsAlertDialog}
    </>
  );
}

const useMqStyles = createMqStyles({
  customizationContent: {
    $base: {
      paddingHorizontal: 16,
      paddingBottom: 16,
      gap: 24,
    },
  },
  divider: {
    $base: {
      height: 1,
      backgroundColor: tokens.colors.$blackOpacity04,
    },
  },
});
